/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  ColorName,
  Colors,
  Icon,
  IconName,
  Img,
  Link,
  Typography,
} from '@zapier/design-system';
import type { UniversalNavLinkType } from '../../../../constants/links';
import { useUniversalNavContext } from '../../../../hooks/useUniversalNavContext';
import { emitUserInteractionEvent } from '../../../../utils/avroEvents';
import { ArrowLinkIcon } from '../../ArrowLinkIcon';

const Styles = {
  root: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 30,
    // Undoes UniversalTopbarLink style, which makes all text bold when toggle is hovered/focused
    fontWeight: 'initial',
    minHeight: 373,
  }),
  heading: css({
    font: `var(--zds-section-header, 600 24px/36px ${Typography.pageHeader1.fontFamily})`,
    display: 'flex',
    flexDirection: 'column',
    color: 'var(--zds-text-stronger, #201515)',
    fontSize: '30px',
    lineHeight: '24px',
    letterSpacing: '1px',
  }),
  subheading: css({
    font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
    color: 'var(--zds-gray-warm-9, #413735)',
    display: 'block',
    marginTop: 8,
  }),
  groupsContainer: css({
    display: 'flex',
    gap: '50px',
  }),
  groupHeading: css({
    font: `var(--zds-section-header, 600 24px/36px ${Typography.pageHeader1.fontFamily})`,
    // TODO: Does not meet minimum contrast ratio
    color: 'var(--zds-gray-warm-5: #B5B2AA)',
    fontSize: '14px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '10px',
  }),
  linkList: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  }),
  linkContainer: css({
    textDecoration: 'none',
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    '&:hover [data-tag="link-heading"]': {
      color: 'var(--zds-brand-orange, #FF4F00)',
      textDecoration: 'none',
    },
    '&:hover [data-tag="link-description"]': {
      textDecoration: 'underline',
    },
    '&:hover [data-tag="arrow-icon-wrapper"]': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: `1px solid var(--zds-ui-primary-strongest, #503EBD)`,
    },
  }),
  iconContainer: (background?: ColorName) =>
    css({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      ...(background && {
        width: 38,
        height: 38,
        borderRadius: '9px',
        backgroundColor: Colors[background],
      }),
    }),
  linkHeading: css({
    font: `var(--zds-body-2, 500 14px/24px ${Typography.base.fontFamily})`,
    color: 'var(--zds-text-stronger, #201515)',
  }),
  linkDescription: css({
    font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
    lineHeight: '20px',
    color: 'var(--zds-gray-warm-9, #413735)',
  }),
};

type MegaMenuProductLeftV2Props = {
  heading?: string;
  subheading?: string;
  submenu: UniversalNavLinkType[];
  /** Name of the parent nav item to be included in tracking event payloads */
  parent?: string;
  /** Function called when closing menu on a link from within the MegaMenu */
  onCloseMenu?: () => void;
};

/**
 * @private
 * Wrapper for the content inside the V2 Product tab's left side
 */
export const MegaMenuProductLeftV2 = ({
  heading,
  onCloseMenu,
  subheading,
  submenu,
  parent,
}: MegaMenuProductLeftV2Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const emitEvent = (link: UniversalNavLinkType, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      interaction_name: `universal_nav_submenu_link_${eventType}`,
      option_selected: link.text,
      interaction_raw_text: link.text,
      destination_href: link.href ?? '',
      custom_tracking_fields: { parent },
    });
  };

  const handleLinkClick = (link: UniversalNavLinkType) => {
    onCloseMenu?.();
    emitEvent(link, 'click');
  };

  const handleLinkHover = (link: UniversalNavLinkType) => {
    emitEvent(link, 'hover');
  };

  return (
    <div css={Styles.root} data-zds>
      {(heading || subheading) && (
        <header data-zds>
          {heading && <span css={Styles.heading}>{heading}</span>}
          {subheading && (
            <span css={Styles.subheading} data-zds>
              {subheading}
            </span>
          )}
        </header>
      )}
      <div css={Styles.groupsContainer} data-zds>
        {submenu.map((group) => (
          <div key={group.text} data-zds>
            <span css={Styles.groupHeading}>{group.text}</span>

            <ul css={Styles.linkList} data-zds>
              {group.submenu?.map((link) => (
                <li key={link.text} data-zds>
                  <Link
                    color={null}
                    css={Styles.linkContainer}
                    href={getFullyQualifiedURL(link.href)}
                    target={link.target}
                    onClick={() => handleLinkClick(link)}
                    onMouseEnter={() => handleLinkHover(link)}
                  >
                    {link.icon ? (
                      <div
                        css={Styles.iconContainer(link.iconBackground)}
                        data-zds
                      >
                        <Icon
                          color={link.iconColor}
                          name={link.icon as IconName}
                          size={19}
                        />
                      </div>
                    ) : (
                      link.serviceIcon && (
                        <Img
                          src={link.serviceIcon.src}
                          alt={`${link.serviceIcon.name} logo`}
                          width={19}
                          height={19}
                          objectFit="contain"
                          borderRadius={0}
                        />
                      )
                    )}
                    <div
                      css={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      data-zds
                    >
                      <span
                        css={Styles.linkHeading}
                        data-tag="link-heading"
                        data-zds
                      >
                        {link.text}
                      </span>
                      {link.description && (
                        <span
                          css={Styles.linkDescription}
                          data-tag="link-description"
                          data-zds
                        >
                          {link.description}
                          <ArrowLinkIcon />
                        </span>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};
